/**
 * Pending invite token persistence helper — Sprint 1 (Chunk 8).
 *
 * Stashes the most recent invite token to localStorage so that:
 *
 *   1. A user who taps an invite link while signed-out, then
 *      completes Google or email sign-in, can be resumed back into
 *      the AcceptInvite flow at app boot — they don't need to
 *      retype the URL.
 *   2. A page refresh mid-flow doesn't lose the token.
 *
 * Implementation is deliberately tiny + sync — it's read at boot in
 * AppNavigator and at mount in AcceptInviteScreen. SecureStorage
 * isn't needed; the token isn't a secret (it's a single-use,
 * 14-day-expiry server-issued capability) and we want sync access.
 */

const KEY = '@founsi/pending_invite_token';

function localStorageOrNull(): Storage | null {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage;
    }
  } catch {
    /* private mode / SSR / native runtime — fall through */
  }
  return null;
}

export function setPendingInviteToken(token: string): void {
  const ls = localStorageOrNull();
  if (!ls) return;
  try {
    ls.setItem(KEY, token);
  } catch {
    /* storage full / disabled — silently drop; user can retry the link */
  }
}

export function getPendingInviteToken(): string | null {
  const ls = localStorageOrNull();
  if (!ls) return null;
  try {
    return ls.getItem(KEY);
  } catch {
    return null;
  }
}

export function clearPendingInviteToken(): void {
  const ls = localStorageOrNull();
  if (!ls) return;
  try {
    ls.removeItem(KEY);
  } catch {
    /* noop */
  }
}

/**
 * Sprint 2.5 (FE-9.4.5) — bootstrap the pending-invite token from
 * the current URL pathname **at app boot**, BEFORE the navigation
 * tree mounts.
 *
 * Why this matters
 * ----------------
 * The deep-link `/invite/{token}` route is only registered inside
 * the authenticated navigation stack (`MainApp.AcceptInvite` in
 * `AppNavigator.tsx`). When an unauthenticated user clicks the
 * email invite link, React Navigation can't match the URL — it
 * falls back to the auth stack's default landing (Login screen),
 * dropping the token on the floor. The user signs in, lands on
 * Home, and never sees the invite.
 *
 * This helper closes the gap. Called synchronously from `App.tsx`
 * before AppNavigator mounts. If `window.location.pathname` matches
 * `/invite/<token>`, we stash the token in localStorage. Then the
 * existing `AppNavigator` boot-resume hook (which runs post-auth
 * and reads `getPendingInviteToken()`) picks it up and routes the
 * user to AcceptInviteScreen with the token preserved.
 *
 * Returns the parsed token (or null) — useful for tests + logging.
 *
 * Idempotent: calling twice with the same URL is harmless. The
 * token gets cleared by `AcceptInviteScreen` on success or after a
 * terminal error (already wired in Sprint 1).
 */
export function bootstrapPendingInviteFromUrl(): string | null {
  try {
    if (typeof window === 'undefined' || !window.location) return null;
    const path = window.location.pathname || '';
    // Match /invite/<token> at the start of the path. Token is any
    // non-empty run of safe URL chars (excludes /, ?, #).
    const m = path.match(/^\/invite\/([^/?#]+)/);
    if (!m) return null;
    const token = decodeURIComponent(m[1]);
    setPendingInviteToken(token);
    return token;
  } catch {
    /* Catch-all defense — malformed URL, etc. Don't break boot. */
    return null;
  }
}
